import React, { useState, useEffect } from 'react';
import { Calendar, ArrowRight, Clock, User, Loader2, AlertCircle, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

import Footer from '../../components/footer/index';

const NewsHomepage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [mainNews, setMainNews] = useState(null);
  const [trendingNews, setTrendingNews] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const { isDarkMode } = useDarkMode();

  // API Base URL
  const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

  // Fungsi untuk membersihkan dan memvalidasi data kategori
  const cleanCategoryData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    return data
      .map(item => {
        // Handle berbagai format data kategori
        if (typeof item === 'string') {
          return item;
        } else if (typeof item === 'object' && item !== null) {
          // Handle format {category: "value"} atau {name: "value"}
          return item.category || item.name || item.value || '';
        }
        return '';
      })
      .filter(category => {
        // Hapus karakter khusus dan pastikan bukan string kosong
        const cleanCategory = category
          .replace(/[”“"']/g, '') // Hapus berbagai jenis kutipan
          .replace(/[^a-zA-Z0-9\s]/g, '') // Hapus karakter khusus
          .trim();
        
        return cleanCategory.length > 0;
      })
      .filter((category, index, self) => self.indexOf(category) === index); // Hapus duplikat
  };

  // Fetch categories dengan error handling yang lebih baik
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/news/categories`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        const cleanedCategories = cleanCategoryData(result.data);
        setCategories(cleanedCategories);
      } else {
        // Fallback categories jika data tidak sesuai format
        setCategories(['Technology', 'Business', 'Lifestyle', 'Health', 'Entertainment']);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback categories jika request gagal
      setCategories(['Technology', 'Business', 'Lifestyle', 'Health', 'Entertainment']);
    }
  };

  // Fungsi untuk mendapatkan nilai yang aman untuk di-render
  const getSafeValue = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') {
      // Bersihkan string dari karakter khusus
      return value.replace(/[”“"']/g, '').trim() || fallback;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') {
      if (value.name) return getSafeValue(value.name, fallback);
      if (value.category) return getSafeValue(value.category, fallback);
      if (value.title) return getSafeValue(value.title, fallback);
      return fallback;
    }
    return fallback;
  };

  // Fetch headline news
  const fetchHeadlineNews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/news/headline`);
      const result = await response.json();
      
      if (result.success && result.data) {
        setMainNews(result.data);
      }
    } catch (error) {
      console.error('Error fetching headline news:', error);
    }
  };

  // Fetch trending news
  const fetchTrendingNews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/news/trending?limit=4`);
      const result = await response.json();
      
      if (result.success && result.data) {
        setTrendingNews(result.data);
      }
    } catch (error) {
      console.error('Error fetching trending news:', error);
    }
  };

  // Fetch news articles
  const fetchNewsArticles = async (page = 1, category = 'all', query = '') => {
    try {
      setLoading(true);
      const categoryParam = category !== 'all' ? `&category=${encodeURIComponent(category)}` : '';
      const searchParam = query ? `&search=${encodeURIComponent(query)}` : '';
      
      const response = await fetch(
        `${API_BASE_URL}/news?page=${page}&limit=9${categoryParam}${searchParam}&status=published`
      );
      const result = await response.json();
      
      if (result.success && result.data) {
        setNewsArticles(result.data.news);
        setPagination(result.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching news articles:', error);
      setError('Gagal memuat artikel berita');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchNewsArticles(1, selectedCategory, searchQuery);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    fetchNewsArticles(1, category, searchQuery);
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      };
      return new Date(dateString).toLocaleDateString('id-ID', options);
    } catch (error) {
      return 'Tanggal tidak valid';
    }
  };

  // Handle article click
  const handleArticleClick = (slug) => {
    window.location.href = `/news/${slug}`;
  };

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchHeadlineNews(),
          fetchTrendingNews(),
          fetchNewsArticles(1, selectedCategory),
          fetchCategories()
        ]);
      } catch (error) {
        setError('Gagal memuat data berita');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  if (loading && newsArticles.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#1E9B4C]" />
          <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Memuat berita...
          </p>
        </div>
      </div>
    );
  }

  if (error && newsArticles.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-500" />
          <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {error}
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 bg-[#1E9B4C] hover:bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      
      {/* Hero Section */}
      <div 
        className="relative h-96 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://telnet.net.id/assets/bg.png')`
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center text-center px-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Portal Berita Telnet Home
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Informasi terkini seputar teknologi, layanan, dan perkembangan terbaru
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder="Cari berita..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 rounded-full bg-white/90 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#1E9B4C] pr-12"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#1E9B4C] hover:bg-green-700 text-white p-2 rounded-full"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Categories Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Kategori Berita
            </h2>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#1E9B4C] text-white'
                  : isDarkMode 
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
              }`}
            >
              Semua
            </button>
            
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-[#1E9B4C] text-white'
                    : isDarkMode 
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                }`}
              >
                {getSafeValue(category)}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2">
            {/* Featured/Headline News */}
            {mainNews && (
              <div 
                className={`rounded-2xl overflow-hidden shadow-lg mb-12 cursor-pointer group ${
                  isDarkMode ? 'bg-gray-800' : 'bg-white'
                }`}
                onClick={() => handleArticleClick(mainNews.slug)}
              >
                <div className="relative">
                  <img 
                    src={getSafeValue(mainNews.image, 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=400&fit=crop')} 
                    alt={getSafeValue(mainNews.title)}
                    className="w-full h-64 md:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=400&fit=crop';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="mb-4">
                      <span className="bg-[#1E9B4C] text-white px-3 py-1 rounded-full text-sm font-medium">
                        {getSafeValue(mainNews.category, 'Berita')}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight">
                      {getSafeValue(mainNews.title)}
                    </h2>
                    <p className="text-gray-200 mb-6 line-clamp-2">
                      {getSafeValue(mainNews.summary)}
                    </p>
                    <div className="flex items-center text-gray-300 text-sm">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span className="mr-4">{formatDate(mainNews.publishedAt || mainNews.createdAt)}</span>
                      <User className="w-4 h-4 mr-1" />
                      <span>{getSafeValue(mainNews.author, 'Admin Telnet Home')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* News Grid */}
            <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {searchQuery ? `Hasil pencarian: "${searchQuery}"` : 'Berita Terbaru'}
            </h3>
            
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div 
                    key={i} 
                    className={`rounded-xl overflow-hidden shadow-md ${
                      isDarkMode ? 'bg-gray-800' : 'bg-white'
                    }`}
                  >
                    <div className="h-48 bg-gray-300 animate-pulse"></div>
                    <div className="p-6">
                      <div className="h-6 bg-gray-300 animate-pulse rounded mb-4"></div>
                      <div className="h-4 bg-gray-300 animate-pulse rounded mb-2"></div>
                      <div className="h-4 bg-gray-300 animate-pulse rounded w-3/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : newsArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {newsArticles.map((article) => (
                  <div 
                    key={article._id} 
                    className={`rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group ${
                      isDarkMode ? 'bg-gray-800' : 'bg-white'
                    }`}
                    onClick={() => handleArticleClick(article.slug)}
                  >
                    <div className="relative overflow-hidden">
                      <img 
                        src={getSafeValue(article.image, 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop')} 
                        alt={getSafeValue(article.title)}
                        className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop';
                        }}
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#1E9B4C] text-white px-2 py-1 rounded text-xs font-medium">
                          {getSafeValue(article.category, 'Berita')}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h4 className={`text-lg font-bold mb-3 group-hover:text-[#1E9B4C] transition-colors ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {getSafeValue(article.title)}
                      </h4>
                      <p className={`mb-4 line-clamp-2 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {getSafeValue(article.summary)}
                      </p>
                      <div className={`flex items-center justify-between text-sm ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          <span>{formatDate(article.publishedAt || article.createdAt)}</span>
                        </div>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {article.readingTime || '3'} min read
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className={`text-xl font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Tidak ada berita ditemukan
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Coba gunakan kata kunci lain atau lihat kategori lainnya
                </p>
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex justify-center mt-12">
                <div className={`flex items-center gap-2 rounded-lg p-2 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} disabled:opacity-50`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  {[...Array(pagination.totalPages)].map((_, i) => {
                    const page = i + 1;
                    // Show limited pages with ellipsis
                    if (
                      page === 1 || 
                      page === pagination.totalPages || 
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 rounded-md ${
                            currentPage === page
                              ? 'bg-[#1E9B4C] text-white'
                              : isDarkMode 
                                ? 'hover:bg-gray-700' 
                                : 'hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-1">...</span>;
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
                    disabled={currentPage === pagination.totalPages}
                    className={`p-2 rounded-md ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} disabled:opacity-50`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-8">
            {/* Trending News */}
            <div className={`rounded-xl p-6 shadow-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <span className="w-2 h-6 bg-[#1E9B4C] rounded-full"></span>
                Trending Now
              </h3>
              <div className="space-y-4">
                {trendingNews.map((news, index) => (
                  <div 
                    key={news._id} 
                    className="group cursor-pointer"
                    onClick={() => handleArticleClick(news.slug)}
                  >
                    <div className={`flex items-start gap-3 pb-4 border-b last:border-b-0 last:pb-0 ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-red-500' : 
                        index === 1 ? 'bg-orange-500' : 
                        index === 2 ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-medium group-hover:text-[#1E9B4C] transition-colors leading-tight mb-1 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {getSafeValue(news.title)}
                        </h4>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {formatDate(news.publishedAt || news.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Newsletter Subscription */}
            <div className={`rounded-xl p-6 shadow-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Berlangganan Newsletter
              </h3>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Dapatkan update berita terbaru langsung ke email Anda
              </p>
              <form className="space-y-3">
                <input
                  type="email"
                  placeholder="Alamat email Anda"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-[#1E9B4C] focus:border-transparent`}
                />
                <button
                  type="submit"
                  className="w-full bg-[#1E9B4C] hover:bg-green-700 text-white font-medium py-2 rounded-lg transition-colors"
                >
                  Berlangganan
                </button>
              </form>
            </div>

            {/* Popular Tags */}
            <div className={`rounded-xl p-6 shadow-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Tag Populer
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 10).map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    #{getSafeValue(category)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NewsHomepage;
